<template>
  <ModalOverlay
    :closeCallback="handleClose"
    :handleAfterEnter="handleAfterEnter"
    :handleAfterLeave="handleAfterLeave"
    :preventCloseOnOverlayClick="preventCloseOnOverlayClick"
    :visible="visible"
  >
    <div
      v-if="visible"
      :class="$style.modal"
      :style="getInlineStyles"
    >
      <div>
        <ModalHeader
          :handleClose="handleClose"
          :headerIcon="headerIcon"
          :headerSubtext="headerSubtext"
          :headerText="headerText"
          :isUserImportModal="isUserImportModal"
          :showClose="showClose"
          :subTextUnderTitle="subTextUnderTitle"
          :useHeaderIconStyleClass="useHeaderIconStyleClass"
        >
          <template #disclaimer>
            <slot name="disclaimer" />
          </template>
        </ModalHeader>
        <div :class="[ $style.modalBody, bodyClass, {[$style.modalBodyCompressed]: compressed} ]">
          <slot />
        </div>
        <ModalFooter
          v-if="shouldShowFooter"
          :cancelText="cancelText"
          :enableMainAction="enableMainAction"
          :handleClose="handleClose"
          :handleMainAction="handleMainAction"
          :kbLink="kbLink"
          :kbLinkText="kbLinkText"
          :loading="loading"
          :mainActionAttention="mainActionAttention"
          :mainActionText="mainActionText"
        >
          <template v-slot:footerComponent>
            <slot name="footerComponentSlot" />
          </template>
        </ModalFooter>
      </div>
    </div>
  </ModalOverlay>
</template>

<script>
/**
 * @example
 * <template>
 *   <div>
 *     <button @click.prevent="handleButtonClick">Display Modal</button>
 *     <Modal
 *       v-model="modalData.visible"
 *       buttonText="OK"
 *       headerText="Modal Header">
 *       <p>This is the body of the modal dialog</p>
 *     </Modal>
 *   </div>
 * </template>
 * import Modal from 'client/components/Modal/Modal.vue';
 *
 * export default {
 *   name: 'ModalTest',
 *   data() {
 *     return {
 *       modalData: {
 *         visible: false,
 *       },
 *     };
 *   },
 *   components: {
 *     Modal,
 *   },
 *   methods: {
 *     handleButtonClick() {
 *       this.modalData.visible = true;
 *     },
 *   },
 * };
 */
import ModalFooter from 'client/components/Modal/ModalComponents/ModalFooter.vue';
import ModalHeader from 'client/components/Modal/ModalComponents/ModalHeader.vue';
import ModalOverlay from 'client/components/Modal/ModalComponents/ModalOverlay.vue';

export default {
  name: 'Modal',

  components: {
    ModalFooter,
    ModalHeader,
    ModalOverlay,
  },

  inject: ['$validator'],
  /**
   * The Modal component uses the `visible` property as its model. A parent component that uses
   * the Modal can use `v-model` to control the visibility of the Modal. In the Modal
   * component itself, the visibility can be controlled by emitting the `updateVisibility` event.
   * @see https://vuejs.org/v2/guide/components-custom-events.html#Customizing-Component-v-model
   */
  model: {
    prop: 'visible',
    event: 'updateVisibility',
  },

  props: {
    backgroundColor: {
      type: String,
      default: undefined,
    },
    bodyClass: {
      type: [String, Array],
      default: '',
    },
    cancelText: {
      type: String,
      default: 'cancel',
    },
    compressed: {
      type: Boolean,
      default: false,
    },
    enableMainAction: {
      type: Boolean,
      default: true,
    },
    footerDisclaimer: {
      type: String,
      default: '',
    },
    handleMainAction: {
      type: Function,
      default: () => {},
    },
    headerIcon: {
      type: Object,
      default: undefined,
    },
    headerSubtext: {
      type: String,
      default: '',
    },
    headerText: {
      type: String,
      default: '',
    },
    height: {
      type: [Number, String],
      default: undefined,
    },
    isUserImportModal: {
      type: Boolean,
      default: false,
    },
    kbLink: {
      type: String,
      default: '',
    },
    kbLinkText: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    mainActionAttention: {
      type: Boolean,
      default: false,
    },
    mainActionText: {
      type: String,
      default: 'continue',
    },
    minHeight: {
      type: [Number, String],
      default: undefined,
    },
    minWidth: {
      type: [Number, String],
      default: undefined,
    },
    preventCloseOnOverlayClick: {
      type: Boolean,
      default: false,
    },
    shouldShowFooter: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    subTextUnderTitle: {
      default: false,
      type: Boolean,
    },
    useHeaderIconStyleClass: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    width: {
      type: [Number, String],
      default: undefined,
    },
  },

  computed: {
    getInlineStyles() {
      const props = [
        { propName: 'backgroundColor', cssPropName: 'background-color' },
        { propName: 'height', cssPropName: 'height' },
        { propName: 'minHeight', cssPropName: 'min-height' },
        { propName: 'minWidth', cssPropName: 'min-width' },
        { propName: 'width', cssPropName: 'width' },
      ];

      const inlineStyles = [];

      props.forEach((prop) => {
        const { propName, cssPropName } = prop;
        let value;

        if (typeof this[propName] === 'number') {
          value = `${parseInt(this[propName], 10)}px`;
        } else if (this[propName]) {
          value = this[propName];
        }

        if (value) {
          inlineStyles.push({ [cssPropName]: value });
        }
      });

      // If any of the inline styles were set then manually unset the max-width too.
      // TODO: Review whether the default Modal styles _should_ actually set the
      // max-width and min-width properties.
      if (inlineStyles.length) {
        inlineStyles.push({ 'max-width': 'unset' });
      }

      return inlineStyles;
    },
  },

  methods: {
    handleAfterEnter() {
      this.$emit('opened');
    },

    handleAfterLeave() {
      this.$emit('closed');
    },

    handleClose() {
      this.$emit('updateVisibility', false);
    },
  },
};
</script>
<style scoped module>
@import './css/ModalStyles.css';

@value (modalZIndex) from '~css/z-index.css';

.modal {
  background-color: white;
  border: none;
  border-radius: 3px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
  max-width: 600px;
  min-width: 500px;
  padding: 0;
  text-align: left;
  z-index: modalZIndex;
}

.modalBody {
  font-size: 1.1em;
  font-weight: 400;
  height: calc(100% - 100px);
  max-height: calc(90vh - 100px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 28px 35px 0;
}

.modalBodyCompressed {
  padding-top: 0;
}
</style>
